'use client';

import {
	Dialog,
	DialogActionButton,
	DialogBody,
	DialogCloseButton,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogSubTitle,
	DialogTitle
} from '@pickleballinc/ui/components/ui/dialog-v2';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { UpcomingMatchup } from '@/infrastructure/dto/UpcomingMatchupDto';
import { dismissModal, isModalDismissed } from '@/lib/cookies';

interface UpcomingMatchupModalProps {
	matchup: UpcomingMatchup;
}

export const UpcomingMatchupModal = ({ matchup }: UpcomingMatchupModalProps) => {
	const router = useRouter();
	const [isOpen, setIsOpen] = useState(false);
	const modalId = `team_league_matchup_${matchup.matchupUuid}`;

	useEffect(() => {
		// Check if modal was dismissed in the last 24 hours
		if (!isModalDismissed(modalId)) {
			setIsOpen(true);
		}
	}, [modalId]);

	const handleClose = () => {
		dismissModal(modalId);
		setIsOpen(false);
	};

	const handleViewSchedule = () => {
		dismissModal(modalId);
		setIsOpen(false);
		// Redirect to the team league schedule page
		const url = matchup.organizationSlug && matchup.teamLeagueSlug ? `/team-leagues/${matchup.teamLeagueSlug}/gameday` : '/';
		router.push(url);
	};

	const title = matchup.isPlayingToday ? 'Playing today' : 'Playing tomorrow';
	const description = matchup.isPlayingToday ? 'You have a matchup today!' : 'You have a matchup tomorrow!';
	const badgeText = matchup.isPlayingToday ? 'Playing today' : 'Playing tomorrow';

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleClose();
				}
			}}
		>
			<DialogContent className="max-w-[560px]" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogSubTitle>{description}</DialogSubTitle>
				</DialogHeader>

				<DialogBody>
					{/* Team Card */}
					<div className="flex w-full items-center rounded-xl border border-gray-200 bg-white px-0 py-4">
						<div className="flex flex-1 items-center gap-6">
							<div className="flex shrink-0 flex-col items-center justify-center">
								{matchup.teamLogoUrl ? (
									<div className="relative h-20 w-40">
										<Image
											src={matchup.teamLogoUrl}
											alt={matchup.teamName || 'Team'}
											fill
											className="object-contain"
											sizes="160px"
										/>
									</div>
								) : (
									<div className="flex h-20 w-40 items-center justify-center bg-gray-100 rounded">
										<span className="text-sm text-gray-500">No logo</span>
									</div>
								)}
							</div>
							<div className="flex flex-1 flex-col gap-3 items-start justify-center min-w-0">
								<div className="flex flex-col items-start w-full">
									<p className="text-xs font-semibold text-gray-900 leading-[18px]">{matchup.divisionTitle}</p>
									<p className="text-xl font-medium text-gray-900 leading-[30px] truncate w-full">{matchup.teamName}</p>
								</div>
								<div className="flex items-center rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5">
									<p className="text-xs font-medium text-[#ff692e] leading-[18px] text-center">{badgeText}</p>
								</div>
							</div>
						</div>
					</div>
				</DialogBody>

				<DialogFooter>
					<DialogCloseButton onClick={handleClose}>Close</DialogCloseButton>
					<DialogActionButton className="bg-orange-500 text-white hover:!bg-orange-700" onClick={handleViewSchedule}>
						View schedule
					</DialogActionButton>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
